import {
  DEFAULT_LOCALE,
  getLocale,
  isLocale,
  setLocale,
  setSupportedLocales,
  type Locale,
} from '@ctcells/i18n'

import { safeStorage } from './storage.ts'

/**
 * 화면 언어 — 고른 값은 이 브라우저에 남는다(테마와 같은 `local` 영역). 서버에는 보내지 않는다.
 *
 * 🔴 **앱이 고를 수 있는 언어를 선언한다**(`initLocale`). 부르지 않는 앱(모듈 3종 · 장비 관점)은 한국어뿐이고, 같은 오리진에
 *    `en` 이 저장돼 있어도 따라가지 않는다 — 판정은 저장값이 아니라 «그 앱의 목록»이다.
 * 🔴 **언어 전환은 새로 고침이다**(`docs/i18n.md`). `t()` 는 순수 함수라 저장만으로는 화면이 안 바뀐다 — Provider 를 넣지 않는다.
 */
export const LOCALE_STORAGE_KEY = 'ctcells:locale'

/**
 * 부팅 때 **화면 모듈이 평가되기 전에** 부른다(`apps/console/src/bootLocale.ts`). 모듈 최상위의 `t()` 도 이 언어를 읽게 하려는 것이다.
 * 저장값이 없거나 이 앱의 목록 밖이면 **그 앱의 기본 언어**(`appDefault`)로 선다.
 *
 * 🔴 **「앱의 기본 언어」와 「모자란 키의 대체 사전」은 다르다**(eric 2026-11-02 「디폴트를 영어로」). 통합 콘솔은 저장값이 없으면
 *    `en` 으로 뜨지만, 영어 사전에 없는 키는 여전히 `DEFAULT_LOCALE`(ko) 사전으로 떨어진다(`t()`). 여기서 `DEFAULT_LOCALE` 을 바꾸지 마라.
 *    `appDefault` 가 목록 밖이면 목록의 첫 언어로 선다 — 고를 수 없는 언어로 부팅하지 않는다.
 */
export function initLocale(
  supported: readonly Locale[],
  appDefault: Locale = DEFAULT_LOCALE,
): Locale {
  setSupportedLocales(supported)
  const stored = safeStorage.read(LOCALE_STORAGE_KEY)
  const fallback = supported.includes(appDefault) ? appDefault : (supported[0] ?? DEFAULT_LOCALE)
  const locale = isLocale(stored) && supported.includes(stored) ? stored : fallback
  setLocale(locale)
  if (typeof document !== 'undefined') document.documentElement.lang = locale
  return locale
}

/** 저장하고 새로 고침한다. 지금 언어를 다시 고르면 아무 일도 안 한다 */
export function changeLocale(
  next: Locale,
  reload: () => void = () => window.location.reload(),
): void {
  if (next === getLocale()) return
  safeStorage.write(LOCALE_STORAGE_KEY, next)
  reload()
}
